import * as React from 'react'
import {
    Box,
    ListItemText,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    IconButton,
    Typography,
    Tooltip,
    Stepper,
    Step,
    StepLabel,
    StepButton,
    StepContent,
    Paper,
    Button,
    Avatar,
} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import {
    ScatterPlotOutlinedIcon,
    ArticleIcon,
    GoogleIcon,
    HubOutlinedIcon,
    BatchPredictionOutlinedIcon,
    AppsIcon,
    PlaylistAddCheckCircleOutlinedIcon,
} from '@mui/icons-material'
import { UserContext } from '../../UserContext'
import { getStepperFlows } from './stepperFlows'
import { useSnackbar } from '../Snackbar'
import HintDot from './HintDot'
import hintsList from './hintsList'
import { sendToAnalytics } from '../../Analytics'
import { DialogsContext } from '../../DialogsContext'
import { useOnboardingStore } from '../../stores/useOnboardingStore'

const selectorShowSnackbar = state => state.showSnackbar

// Origin of an `ai_action`, derived from the AI widget's emitted `mode`. Lets
// analytics tell apart a graph-forced auto-generation from a user's own submit
// and from incidental AI-panel UI clicks. Graph-button clicks are a separate
// event stream (infranodus-graph, `source: "ai_menu"`); here we only classify
// what the AI panel emits over myEventEmitter.
//   - graph:    host-forced request (forceRequestMode → applyForceRequestModeSend)
//   - user:     user typed a message and submitted it
//   - ai_panel: any other panel interaction (save, copy, elaborate, switch tab, …)
const deriveAiActionTrigger = mode => {
    if (mode === 'submit_message_automatically') return 'graph'
    if (mode === 'submit_message_manually') return 'user'
    return 'ai_panel'
}

export default function WorkflowStepper({
    stepperName,
    onWorkflowClick,
    location,
}) {
    const {
        activeImportDialog,
        setActiveImportDialog,
        openImportDialog,
        closeImportDialog,
    } = React.useContext(DialogsContext)

    const [anchorEl, setAnchorEl] = React.useState(null)
    const { username = '-' } = React.useContext(UserContext)
    const open = Boolean(anchorEl)

    const windowInnerWidth = window ? window.innerWidth : 1200
    const [width, setWidth] = React.useState(windowInnerWidth)

    const isMobile = width <= 768

    const stepperButton = React.useRef(null)

    const [stepperType, setStepperType] = React.useState('basic')

    const showSnackbar = useSnackbar(selectorShowSnackbar)

    React.useEffect(() => {
        if (stepperName == 'apps') {
            setStepperType(stepperName)
        }
    }, [stepperName])

    const stepperFlows = getStepperFlows()
    const steps = stepperFlows[stepperType].steps

    const [activeStep, setActiveStep] = React.useState(0)
    const [completed, setCompleted] = React.useState({})
    const [shownSteps, setShownSteps] = React.useState({})

    const [, forceUpdate] = React.useReducer(x => x + 1, 0)

    const [completedCount, setCompletedCount] = React.useState(0)

    const [hintsToShow, setHintsToShow] = React.useState([])

    const hintsShown = localStorage ? localStorage.getItem('hintsShown') : null

    const isInitialMount = React.useRef(true)

    React.useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }

        const currentUrl = window.location.pathname

        const allStepsCompletedCount = localStorage.getItem(
            `workflow_steps_completed_count`
        )
            ? JSON.parse(localStorage.getItem(`workflow_steps_completed_count`))
            : {}
        allStepsCompletedCount[currentUrl] = completedCount

        localStorage.setItem(
            `workflow_steps_completed_count`,
            JSON.stringify(allStepsCompletedCount)
        )

        const allStepsCompleted = localStorage.getItem(
            `workflow_steps_completed`
        )
            ? JSON.parse(localStorage.getItem(`workflow_steps_completed`))
            : {}
        allStepsCompleted[currentUrl] = completed

        localStorage.setItem(
            `workflow_steps_completed`,
            JSON.stringify(allStepsCompleted)
        )
    }, [completedCount, completed])

    React.useEffect(() => {
        const currentUrl = window.location.pathname
        const savedCompletedStepsCount = localStorage.getItem(
            'workflow_steps_completed_count'
        )
        if (savedCompletedStepsCount) {
            const allStepsCompletedCount = JSON.parse(savedCompletedStepsCount)
            setCompletedCount(allStepsCompletedCount[currentUrl] || 0)
        }
        const savedCompletedSteps = localStorage.getItem(
            'workflow_steps_completed'
        )
        if (savedCompletedSteps) {
            const allStepsCompleted = JSON.parse(savedCompletedSteps)
            setCompleted(allStepsCompleted[currentUrl] || {})
        }
    }, [])

    const handleHintClose = hintName => {
        if (hintsShown) {
            localStorage.setItem(
                'hintsShown',
                JSON.stringify([...JSON.parse(hintsShown), hintName])
            )
        } else {
            localStorage.setItem('hintsShown', JSON.stringify([hintName]))
        }

        sendToAnalytics({
            userId: username,
            event: 'hint_action',
            data: {
                action: hintName,
            },
        })

        setHintsToShow(prevHints => prevHints.filter(hint => hint !== hintName))

        if (typeof posthog == 'undefined') return
        posthog.capture('hint_action', {
            action: hintName,
        })
    }

    const handleHintsToShow = React.useCallback(
        hintName => {
            if (hintsShown && JSON.parse(hintsShown).includes(hintName)) return
            if (hintsToShow.indexOf(hintName) != -1) return

            setHintsToShow(prevHints =>
                prevHints.includes(hintName)
                    ? prevHints
                    : [...prevHints, hintName]
            )
        },
        [hintsToShow]
    )

    // Held back while the onboarding guide is (or may still turn out to be)
    // active — the checklist popover covers the guide toast on /apps.
    const onboardingStatus = useOnboardingStore(state => state.status)

    React.useEffect(() => {
        if (onboardingStatus === 'unknown' || onboardingStatus === 'active')
            return
        if (stepperType == 'apps' && completedCount < 4 && !isMobile) {
            setAnchorEl(stepperButton.current)
            return
        }
    }, [stepperType, onboardingStatus])

    React.useEffect(() => {
        if (typeof userFactory == 'undefined') return

        const currentApp =
            userFactory.getCurrentApp() != 'edit'
                ? userFactory.getCurrentApp()
                : userFactory.getContextOptions('type')

        if (window.myEventEmitter) {
            myEventEmitter.on('GraphLoaded', () => {
                if (!userFactory.getGraphEmpty()) {
                    if (completed[0] != true) {
                        // handleCompleteStep(0)
                        // setActiveStep(1)
                    }
                }
                sendToAnalytics({
                    userId: username,
                    event: 'graph_action',
                    data: {
                        source: currentApp,
                        action: 'load_graph',
                    },
                })

                handleHintsToShow('graph_nodes')

                if (
                    analyticsPanel &&
                    typeof analyticsPanel != 'undefined' &&
                    analyticsPanel.isVisible()
                ) {
                    handleHintsToShow('analytics_intro')
                } else {
                    handleHintsToShow('analytics_button')
                }

                handleHintsToShow('show_topics')
                handleHintsToShow('ai_insights')
                handleHintsToShow('filter_panel')
            })
            myEventEmitter.on('Modularity', () => {
                // if (completed[0] != true) {
                //    // handleCompleteStep(0)
                // }
                sendToAnalytics({
                    userId: username,
                    event: 'graph_action',
                    data: {
                        source: currentApp,
                        action: 'calculate_modularity',
                    },
                })
            })
            myEventEmitter.on('workflowIsVariable', () => {
                // if (
                //     stepperType == 'diagnostics' ||
                //     stepperType == 'keywordResearch'
                // )
                // handleCompleteStep(1)
            })

            myEventEmitter.on('onAnalyticsAction', (action, flag) => {
                // if (
                //     stepperType == 'diagnostics' &&
                //     action == 'select_community'
                // )
                // handleCompleteStep(5)
                // if (
                //     stepperType == 'diagnostics' &&
                //     action == 'select_influential_nodes'
                // )
                // handleCompleteStep(4)

                sendToAnalytics({
                    userId: username,
                    event: 'analytics_action',
                    data: {
                        source: currentApp,
                        action: action,
                        flag: flag || '',
                    },
                })
            })

            myEventEmitter.on('submitProjectNotes', times => {
                // if (stepperType == 'diagnostics' && times == 1)
                // handleCompleteStep(6)

                sendToAnalytics({
                    userId: username,
                    event: 'project_notes_action',
                    data: {
                        source: currentApp,
                        action: 'submit',
                    },
                })

                sendToAnalytics({
                    userId: username,
                    event: 'value_action',
                    data: {
                        original_event: 'project_notes_action',
                        source: currentApp,
                        action: 'submit_project_notes',
                    },
                })
            })

            myEventEmitter.on('onGraphAction', receivedData => {
                sendToAnalytics({
                    userId: username,
                    event: 'graph_action',
                    data: { ...receivedData, source: currentApp },
                })

                if (
                    receivedData.action == 'remove_influential_nodes' ||
                    receivedData.action == 'hide_node' ||
                    receivedData.action == 'hide_nodes' ||
                    receivedData.action == 'click_node' ||
                    receivedData.action == 'save_graph_version' ||
                    receivedData.action == 'download'
                ) {
                    sendToAnalytics({
                        userId: username,
                        event: 'value_action',
                        data: {
                            original_event: 'graph_action',
                            source: currentApp,
                            action: receivedData.action,
                        },
                    })

                    if (receivedData.action == 'click_node') {
                        handleHintsToShow('delete_nodes')
                    }

                    if (receivedData.action == 'hide_node') {
                        setTimeout(() => {
                            handleHintsToShow('unhide_nodes')
                        }, 2000)
                    }
                }
            })

            myEventEmitter.on('onStatementsFilterAction', receivedData => {
                sendToAnalytics({
                    userId: username,
                    event: 'statements_filter_action',
                    data: { ...receivedData, source: currentApp },
                })
            })

            myEventEmitter.on('onStatementsAction', receivedData => {
                sendToAnalytics({
                    userId: username,
                    event: 'statements_action',
                    data: { ...receivedData, source: currentApp },
                })

                if (
                    receivedData.action == 'save_to_favorite' ||
                    receivedData.action == 'change_privacy_graph'
                ) {
                    sendToAnalytics({
                        userId: username,
                        event: 'value_action',
                        data: {
                            save_to_ai: true,
                            original_event: 'statements_action',
                            source: 'statements_menu',
                            action: receivedData.action,
                        },
                    })
                }
            })

            myEventEmitter.on('onAiAction', receivedData => {
                const trigger = deriveAiActionTrigger(receivedData.mode)
                sendToAnalytics({
                    userId: username,
                    event: 'ai_action',
                    data: {
                        ...receivedData,
                        source: currentApp,
                        action: receivedData.mode,
                        trigger,
                        automatic: trigger == 'graph',
                    },
                })

                if (
                    receivedData.mode == 'save_to_notes' ||
                    receivedData.mode == 'save_to_graph' ||
                    receivedData.mode == 'elaborate' ||
                    receivedData.mode == 'copy_message'
                ) {
                    sendToAnalytics({
                        userId: username,
                        event: 'value_action',
                        data: {
                            save_to_ai: true,
                            original_event: 'ai_action',
                            source: 'ai_panel',
                            mode: receivedData.mode,
                            action: receivedData.mode,
                        },
                    })
                }
            })

            myEventEmitter.on('onCopyAction', receivedData => {
                sendToAnalytics({
                    userId: username,
                    event: 'copy_action',
                    data: { ...receivedData, source: currentApp },
                })

                console.log('receivedData', receivedData)
                sendToAnalytics({
                    userId: username,
                    event: 'value_action',
                    data: {
                        ...receivedData,
                        save_to_ai: true,
                        original_event: 'copy_action',
                        source: receivedData.source,
                        action: 'copy_to_clipboard',
                    },
                })
            })

            myEventEmitter.on('showSnackbar', receivedData => {
                showSnackbar(
                    'info',
                    receivedData.message,
                    'top',
                    receivedData.timeout
                )
            })

            myEventEmitter.on('onNotesAction', receivedData => {
                sendToAnalytics({
                    userId: username,
                    event: 'notes_action',
                    data: { ...receivedData, app: currentApp },
                })
            })
        }

        switch (currentApp) {
            case 'seo':
                setStepperType('seo')
                break
            case 'niche':
                setStepperType('niche')
                break
            case 'kwrds':
                setStepperType('keywordResearch')
                break
            case 'pdf':
                setStepperType('pdf')
                break
            case 'csv':
                setStepperType('csv')
                break
            case 'txt':
            case 'md':
                setStepperType('txt')
                break
            case 'rss':
                setStepperType('rss')
                break
            case 'google':
                setStepperType('google')
                break
            default:
                setStepperType('basic')
                break
        }

        return

        // This is the old logic for showing the workflow stepper

        if (!localStorage) return

        const workflowStepperShown = localStorage.getItem(
            'workflowStepperShown'
        )

        const shouldOpenOnLoad = getLoadWorkflowParam() == true && !isMobile

        // ||
        // (userFactory.getGraphEmpty() &&
        //     (currentApp == 'kwrds' ||
        //         (currentApp == 'edit' &&
        //             !userFactory.getGraphOption('mode'))))

        if (workflowStepperShown && !shouldOpenOnLoad) return

        if (
            typeof userFactory != 'undefined' &&
            userFactory.getGraphEmpty() &&
            !shouldOpenOnLoad
        )
            return
        if (
            typeof userFactory != 'undefined' &&
            userFactory.isContextPublic() == true &&
            !shouldOpenOnLoad
        )
            return

        setAnchorEl(stepperButton.current)
        localStorage.setItem('workflowStepperShown', true)

        sendToAnalytics({
            userId: username,
            event: 'workflow_action',
            data: {
                source: currentApp,
                action: 'show_workflow_stepper',
            },
        })

        if (typeof posthog == 'undefined') return
        posthog.capture('workflow_action', {
            action: 'show_workflow_stepper',
        })
    }, [])

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth)
    }

    const handleAnchorClick = event => {
        setAnchorEl(event.currentTarget)
        onWorkflowClick(stepperType)

        if (typeof posthog == 'undefined') return
        posthog.capture('workflow_click', {
            action: stepperType,
        })
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const totalSteps = () => {
        return steps.length
    }

    const percentageSteps = () => {
        return parseInt((completedCount / totalSteps()) * 100) + '%'
    }

    const completedSteps = () => {
        return Object.keys(completed).length
    }

    const isLastStep = () => {
        return activeStep === totalSteps() - 1
    }

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps()
    }

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed,
                  // find the first step that has been completed
                  steps.findIndex((step, i) => !(i in completed))
                : activeStep + 1
        setActiveStep(newActiveStep)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => () => {
        setActiveStep(step)

        sendToAnalytics({
            userId: username,
            event: 'workflow_action',
            data: {
                action: 'change_step',
                step: step,
            },
        })

        if (typeof posthog == 'undefined') return
        posthog.capture('workflow_action', {
            action: 'change_step',
        })
    }

    const handleComplete = () => {
        const newCompleted = completed
        newCompleted[activeStep] = true
        setCompleted(newCompleted)
        const totalStepsCompleted = Object.keys(completed)
            .map(key => completed[key])
            .filter(value => value).length
        setCompletedCount(totalStepsCompleted)

        // Only increment if we haven't reached the last step
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1)
        }
    }
    const handleCompleteStep = stepNumber => {
        const newCompleted = completed
        newCompleted[stepNumber] = true
        setCompleted(newCompleted)
        const totalStepsCompleted = Object.keys(completed)
            .map(key => completed[key])
            .filter(value => value).length
        setCompletedCount(totalStepsCompleted)
    }
    const handleUncomplete = () => {
        const newUncompleted = completed
        newUncompleted[activeStep] = false
        setCompleted(newUncompleted)
        const totalStepsCompleted = Object.keys(completed)
            .map(key => completed[key])
            .filter(value => value).length
        setCompletedCount(totalStepsCompleted)
    }

    const handleReset = () => {
        setActiveStep(0)
        setCompleted({})
        setCompletedCount(0)
    }

    const handleShowStep = () => {
        const newShown = shownSteps
        newShown[activeStep] = true
        setShownSteps(newShown)

        const showEventName = steps[activeStep].showEvent.show

        if (
            typeof myEventEmitter != 'undefined' &&
            myEventEmitter._events[showEventName]
        ) {
            myEventEmitter.emit(showEventName)
        } else {
            switch (showEventName) {
                case 'openBrainstormDialog':
                    setActiveImportDialog('aiImport')
                    break
                case 'openKnowledgeGraphDialog':
                    setActiveImportDialog('aiGraphs')
                    break
                case 'openGoogleSearchResultsDialog':
                    setActiveImportDialog('google')
                    break
                case 'openKeywordResearchDialog':
                    setActiveImportDialog('keywords')
                    break
                case 'openTextAnalysisDialog':
                    setActiveImportDialog('import')
                    break
                case 'openMarketGapDialog':
                    setActiveImportDialog('niche')
                    break
            }
        }

        forceUpdate()

        if (steps[activeStep].showEvent.closeOnShow) setAnchorEl(null)
    }

    const handleUnshowStep = () => {
        const newShown = shownSteps
        newShown[activeStep] = false
        setShownSteps(newShown)

        if (
            typeof myEventEmitter != 'undefined' &&
            myEventEmitter._events[steps[activeStep].showEvent.undo]
        ) {
            myEventEmitter.emit(steps[activeStep].showEvent.undo)
        }

        forceUpdate()
    }

    const handleLearnMore = link => {
        if (!link) return

        window.open(link, '_blank')
    }

    return (
        <React.Fragment>
            {hintsToShow &&
                hintsToShow.map((hintName, index) => (
                    <HintDot
                        key={`hintName-${hintName}-${index}`}
                        hint={hintsList[hintName].hint}
                        targetId={hintsList[hintName].target_id}
                        position={hintsList[hintName].position}
                        onHintClose={() => handleHintClose(hintName)}
                    />
                ))}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'right',
                    marginLeft: !isMobile ? 3 : 0,
                }}
            >
                {location == 'apps' && !isMobile && (
                    <Typography
                        sx={{
                            marginRight: 1,
                            color: 'text.primary',
                            cursor: 'pointer',
                        }}
                        onClick={handleAnchorClick}
                    >
                        tasks
                    </Typography>
                )}
                {!isMobile && (
                    <Tooltip
                        title="Recommended research workflow"
                        arrow={false}
                    >
                        <IconButton
                            onClick={handleAnchorClick}
                            size="small"
                            sx={{
                                ml: 0,
                                mr: '2px',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '50%',
                                    background:
                                        completedCount > 1
                                            ? `conic-gradient(from 0deg, #4CAF50 ${percentageSteps()}, rgba(76, 175, 80, 0.3) 0%)`
                                            : `conic-gradient(from 0deg, #00B7FF ${percentageSteps()}, rgba(0, 183, 255, 0.3) 0%)`,
                                    animation:
                                        completedCount <= 1
                                            ? 'rotate 2s linear infinite'
                                            : 'none',
                                },
                                '@keyframes rotate': {
                                    '0%': {
                                        filter: 'hue-rotate(0deg)',
                                    },
                                    '100%': {
                                        filter: 'hue-rotate(360deg)',
                                    },
                                },
                            }}
                            id="stepperButton"
                            ref={stepperButton}
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        background: '#fff',
                                        zIndex: 0,
                                    },
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        background:
                                            'linear-gradient(135deg, #00B7FF 0%, #0091ff 100%)',
                                        color: '#ffffff',
                                        boxShadow:
                                            '0 2px 10px rgba(0, 183, 255, 0.2)',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        zIndex: 1,
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            boxShadow:
                                                '0 4px 20px rgba(0, 183, 255, 0.4)',
                                            background:
                                                'linear-gradient(135deg, #00B7FF 20%, #0091ff 120%)',
                                        },
                                    }}
                                >
                                    {completedCount}/{totalSteps()}
                                </Avatar>
                            </Box>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Menu
                sx={{ width: 400 }}
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflowY: 'scroll',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: stepperType == 'apps' ? '120px' : '40px',
                        width: 400,
                        pt: '8px',
                        pl: '24px',
                        pr: '24px',
                        borderRadius: 2,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography variant="h6">
                        {stepperFlows[stepperType].name}
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
                {/* Modern Progress Indicator */}
                <Box sx={{ width: '100%', mb: 2, mt: 2 }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: 'grey.100',
                            borderRadius: 3,
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                borderRadius: 3,
                                bgcolor: 'lightgreen',
                                width: `${(completedCount / totalSteps()) *
                                    100}%`,
                                transition: 'width 0.3s ease-in-out',
                            }}
                        />
                    </Box>
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                        {completedCount} of {totalSteps()} tasks completed
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        {percentageSteps()}
                    </Typography>
                </Box>

                <Divider sx={{ pt: '4px', mb: '8px' }} />
                <Stepper
                    nonLinear
                    activeStep={activeStep}
                    orientation="vertical"
                    sx={{ mb: '32px' }}
                >
                    {steps.map((step, index) => (
                        <Step key={step.label} completed={completed[index]}>
                            <StepButton
                                color="inherit"
                                onClick={handleStep(index)}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                {completed[index] ? (
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            textDecorationLine: 'line-through',
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                ) : (
                                    <Typography variant="subtitle1">
                                        {step.label}
                                    </Typography>
                                )}
                            </StepButton>

                            <StepContent>
                                <Typography variant="body2">
                                    {step.description}
                                </Typography>
                                {step.menuHint && (
                                    <Box sx={{ pt: 1, pb: 0.5 }}>
                                        <Typography variant="caption">
                                            {step.menuHint}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ mb: 2 }}>
                                    <div>
                                        {step.showEvent &&
                                            !completed[activeStep] &&
                                            (!shownSteps[activeStep] ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleShowStep}
                                                    sx={{ mt: 1, mr: 1 }}
                                                >
                                                    Show
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleUnshowStep}
                                                    sx={{ mt: 1, mr: 1 }}
                                                >
                                                    Undo
                                                </Button>
                                            ))}
                                        {step.learnMore &&
                                            !completed[activeStep] && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() =>
                                                        handleLearnMore(
                                                            step.learnMore
                                                        )
                                                    }
                                                    sx={{ mt: 1, mr: 1 }}
                                                >
                                                    Learn More
                                                </Button>
                                            )}
                                        {activeStep !== steps.length &&
                                            (completed[activeStep] ? (
                                                <Box
                                                    sx={{
                                                        display: 'inline',
                                                    }}
                                                >
                                                    <Button
                                                        sx={{
                                                            mt: 1,
                                                            mr: 0,
                                                        }}
                                                        disabled={true}
                                                        size="small"
                                                    >
                                                        Step {activeStep + 1}{' '}
                                                        Completed
                                                    </Button>
                                                    <Button
                                                        onClick={
                                                            handleUncomplete
                                                        }
                                                        size="small"
                                                        sx={{
                                                            mt: 1,
                                                            mr: 1,
                                                        }}
                                                    >
                                                        Redo
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Button
                                                    onClick={handleComplete}
                                                    size="small"
                                                    variant="contained"
                                                    sx={{ mt: 1, mr: 1 }}
                                                >
                                                    {completedSteps() ===
                                                    totalSteps() - 1
                                                        ? 'Finish'
                                                        : step.learnMore
                                                        ? 'Complete'
                                                        : 'Mark as Completed'}
                                                </Button>
                                            ))}

                                        <Button
                                            onClick={handleNext}
                                            size="small"
                                            sx={{ mt: 1, mr: 0 }}
                                        >
                                            {index === steps.length - 1
                                                ? 'Finish'
                                                : 'Next'}
                                        </Button>
                                    </div>
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
                {allStepsCompleted() && (
                    <Paper square elevation={0} sx={{ p: 3 }}>
                        <Typography>
                            All tasks completed - well done!
                        </Typography>
                        <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                            Start Again
                        </Button>
                    </Paper>
                )}
            </Menu>
        </React.Fragment>
    )
}

const getLoadWorkflowParam = () => {
    const urlParameters = new Proxy(
        new URLSearchParams(window.location.search),
        {
            get: (searchParams, prop) => searchParams.get(prop),
        }
    )

    if (urlParameters.loadWorkflow) {
        return true
    }
    return false
}
